import React, { useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import { useTheme } from 'styled-components'
import { IoClose } from 'react-icons/io5'
import { useResponsive,useVideoPlayer} from 'shared-ui/hooks'
import { media } from 'shared-ui/utils/media'
import { useLocation } from 'react-router-dom'
import { ROUTES, HOW_IT_WORKS, TUTORIAL_VIDEO_URLS, GUIDED_TOUR_AND_QUICK_TIPS, getDynamicVideoUrls } from 'shared-ui/utils/constants'
import { useSelector } from 'react-redux'
import Player from '../../../../../apps/super-app/src/pages/Player'
import {PlayVideoIcon,PauseVideoIcon,ForwardIcon,RewindIcon,MuteIcon,VolumeIcon,FullscreenIcon} from "shared-ui/assets"

const ToolsTutorialPopup = ({
  isOpen,
  onClose,
  selectedTool,
  customVideoSources,
  isHowItWorksFromHome,
  aiToolId
}) => {
  const theme = useTheme()
  const swiperRef = useRef(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480)
  const isMobileDevice = useResponsive(media.mobile)
  const location = useLocation()
  const howItWorks = useSelector((state) => state.homeData.howItWorks)
  const [isFullscreen, setIsFullscreen] = useState(false)

  // Define video sources based on selected tool
  const getVideoSources = () => {
    if (customVideoSources && customVideoSources.length) {
      return customVideoSources
    }
    const dynamicUrls = getDynamicVideoUrls(howItWorks, aiToolId, true)
    if (dynamicUrls && dynamicUrls.length > 0) {
      return dynamicUrls
    }
    return (
      TUTORIAL_VIDEO_URLS.MOBILE[aiToolId] || TUTORIAL_VIDEO_URLS.MOBILE.DEFAULT
    )
  }

  const getVideoSourcesDesktop = () => {
    if (customVideoSources && customVideoSources.length) {
      return customVideoSources
    }
    const dynamicUrls = getDynamicVideoUrls(howItWorks, aiToolId, false)
    if (dynamicUrls && dynamicUrls.length > 0) {
      return dynamicUrls
    }
    return (
      TUTORIAL_VIDEO_URLS.DESKTOP[aiToolId] ||
      TUTORIAL_VIDEO_URLS.DESKTOP.DEFAULT
    )
  }

  const videoSourcesRaw = isMobileDevice
    ? getVideoSources()
    : getVideoSourcesDesktop()
  const videoSources = Array.isArray(videoSourcesRaw) ? videoSourcesRaw : []
  const showSlider = videoSources.length > 1
  const tutorialPlayerId = 'tutorial-logix-player'
  const {
    isPlaying,
    togglePlayPause,
    setIsPlaying,
    seekForward,
    seekBackward,
    setShowSeekbar,
    handleSeek,
    duration,
    currentTime,
    isMuted,
    toggleMute
  } = useVideoPlayer({
    isVideo: true,
    videoUrl: videoSources?.[activeIndex] ?? videoSources?.[0],
    autoplay: false,
  })

  const pauseBackgroundVideos = () => {
    document.querySelectorAll('video').forEach((video) => {
      if (!video.closest(`#${tutorialPlayerId}`) && !video.paused) {
        video.pause()
      }
    })
  }

  useEffect(() => {
    if (isOpen) {
      setActiveIndex(0)
      setIsPlaying(false);
      setShowSeekbar(true);
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) pauseBackgroundVideos()
  }, [activeIndex, isOpen])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!isOpen) return undefined

    pauseBackgroundVideos()
    const observer = new MutationObserver(pauseBackgroundVideos)
    observer.observe(document.body, { childList: true, subtree: true })
    document.addEventListener('play', pauseBackgroundVideos, true)

    return () => {
      observer.disconnect()
      document.removeEventListener('play', pauseBackgroundVideos, true)
    }
  }, [isOpen])
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false)
      }
    }

    window.addEventListener('keydown', handleEscape)

    return () => {
      window.removeEventListener('keydown', handleEscape)
    }
  }, [isFullscreen])

  if (!isOpen || !videoSources || videoSources.length === 0) return null
  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext()
    }
  }

  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev()
    }
  }

  const getPlayerProps = (src) => ({
    key: activeIndex,
    videoUrl: src,
    autoplay: false,
    muted: false,
    playerId: tutorialPlayerId,
    playVideoInLoop: false,
    className: 'content-detail-player w-full h-full',
    style: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      background: 'black'
    }
  })


const toggleFullscreen = () => {
  setIsFullscreen((prev) => !prev)
}

const renderPlayer = (src) => (
  <div
    className="group relative w-full overflow-hidden rounded-3xl"
     style={{
    height: isFullscreen
      ? '100vh'
      : isMobile
        ? '480px'
        : '416px',
    maxHeight: isFullscreen
      ? 'none'
      : '55vh'
  }}
  >
    <Player {...getPlayerProps(src)} />

    {/* Center controls */}
    <div
      className={`absolute left-1/2 top-1/2 z-30 flex -translate-x-1/2 -translate-y-1/2 items-center gap-8 transition-opacity duration-300`}
    >
      {/* Rewind */}
      <button
        type="button"
        className="font-semibold disabled:cursor-not-allowed pointer-events-auto flex h-11 w-11 items-center justify-center border-0 bg-transparent p-0 transition hover:scale-105"
        onClick={(e) => {
          e.stopPropagation()
          seekBackward()
        }}
      >
        <RewindIcon height={32} width={32} />
      </button>

      {/* Play / Pause */}
      <button
        type="button"
        className="font-semibold disabled:cursor-not-allowed pointer-events-auto flex  items-center justify-center border-0 bg-transparent p-0 transition hover:scale-105"
        onClick={(e) => {
          e.stopPropagation()
          togglePlayPause()
        }}
      >
        {isPlaying ? (
          <PauseVideoIcon height={48} width={48}/>
        ) : (
          <PlayVideoIcon height={48} width={48}/>
        )}
      </button>

      {/* Forward */}
      <button
        type="button"
        className="font-semibold disabled:cursor-not-allowed pointer-events-auto flex h-11 w-11 items-center justify-center border-0 bg-transparent p-0 transition hover:scale-105"
        onClick={(e) => {
          e.stopPropagation()
          seekForward()
        }}
      >
        <ForwardIcon height={32} width={32} />
      </button>
    </div>

    {/* Existing seek bar */}
    <div
      className={`absolute ${!isFullscreen ? 'bottom-0' : 'bottom-6'} left-0 z-50 w-full px-6 pb-6 pointer-events-auto transition-opacity duration-300 }`}
    >
      <div
        className={`mx-auto w-full'`}
      >
        {/* Current time / duration */}
        <div className="flex items-center justify-between">
        <div className="mb-2 text-left text-sm text-white/90">
          {`${Math.floor(currentTime / 60)}:${String(
            Math.floor(currentTime % 60)
          ).padStart(2, '0')} / ${Math.floor(
            (duration || 0) / 60
          )}:${String(
            Math.floor((duration || 0) % 60)
          ).padStart(2, '0')}`}
        </div>
        <div className="flex gap-2 cursor-pointer">
            <button className="font-semibold disabled:cursor-not-allowed inline-flex h-9 w-9 items-center justify-center text-white transition hover:text-white/75" onClick={toggleMute}>
              {isMuted ? <MuteIcon /> : <VolumeIcon />}
            </button>
            <button onClick={(e) => {
              e.stopPropagation()
              toggleFullscreen()
            }}
              className="font-semibold disabled:cursor-not-allowed inline-flex h-9 w-9 items-center justify-center text-white transition hover:text-white/75">
              <FullscreenIcon />
            </button>
        </div>
        </div>

        {/* Progress bar */}
        <div
          className="group flex h-4 w-full cursor-pointer items-center"
          onClick={handleSeek}
          onTouchStart={handleSeek}
        >
          <div className="h-1 w-full overflow-hidden rounded bg-white/25">
            <div
              className="h-1 rounded bg-white"
              style={{
                width: `${
                  duration
                    ? (currentTime / duration) * 100
                    : 0
                }%`,
                transition: 'width 0.1s linear'
              }}
            />
          </div>
        </div>
      </div>
    </div>
  </div>
)
const howItWorksText =
  !selectedTool && customVideoSources?.length
    ? HOW_IT_WORKS?.HOWITWORK
    : HOW_IT_WORKS?.HOW_TOOL_NAME_WORKS?.replace('%', selectedTool);

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center "
      style={{ background: theme.app.backdropStyle }}
    >
      <div
        className={`relative w-full p-6 shadow-lg ${isFullscreen
            ? 'max-w-none rounded-none'
            : 'max-w-2xl max-h-[90vh] rounded-2xl'
          }`}
        
        style={{
          background: theme.app.background,
          color: theme.app.text,
          border: `1px solid ${theme.colors.Granite}`,
          width: isMobile
            ? '350px'
            :isFullscreen? "100vw": '60%'
        }}
      >
        <div className="flex justify-between">
          <div
            className={`text-left ${isMobile ? 'text-[22px]' : 'text-[20px] font-semibold'}`}
            style={{
              color: isMobile ? theme.app.iconlabel : theme.app.text
            }}
          >
            {howItWorksText}
          </div>
          <button aria-label="Close" onClick={onClose} className=" p-1">
            <IoClose
              size={isMobile ? 22 : 24}
              style={{ color: theme.app.iconlabel }}
            />
          </button>
        </div>
        <div style={{ marginTop: '24px' }}>
          {showSlider ? (
            <Swiper
              modules={[Pagination]}
              pagination={{ clickable: true }}
              onSwiper={(swiper) => {
                swiperRef.current = swiper
              }}
              onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
              className="tutorial-swiper"
            >
              {videoSources.map((src, i) => (
                <SwiperSlide key={i}>
                  <div className="flex justify-center">
                    {activeIndex === i && renderPlayer(src)}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="flex justify-center">
              {renderPlayer(videoSources[0])}
            </div>
          )}
          <style>{`
                    .tutorial-swiper .swiper-pagination {
                        position: relative !important;
                        margin-top: 24px;
                        text-align: center;
                        
                    }
                        .swiper-pagination-bullet {background-color: ${theme.colors.Granite}; width:8px; height:8px;}
                        .swiper-pagination-bullet-active {background-color: ${theme.colors.Blue};}
                `}</style>

          {/* Navigation - only show for slider */}
          {showSlider && (
            <div className="mt-4 flex justify-between text-blue-400">
              {activeIndex > 0 ? (
                <button onClick={handlePrev} className="text-sm ">
                  {GUIDED_TOUR_AND_QUICK_TIPS?.CTA_BUTTONS?.CTA_PREV}
                </button>
              ) : (
                <span></span>
              )}
              {activeIndex < videoSources.length - 1 ? (
                <button onClick={handleNext} className="text-sm ">
                  {GUIDED_TOUR_AND_QUICK_TIPS?.CTA_BUTTONS?.CTA_NEXT}
                </button>
              ) : (
                <span></span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ToolsTutorialPopup
